import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  IconButton,
  Fade,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import axios from "axios";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";


import {
  API_EVENT_PHOTO_UPLOAD,
  API_DELETE_EVENT_PHOTO,
  API_GET_EVENT_IMAGES,
} from "../../../Util/URLConstants";
import {  VM_Colors } from "../../../Styles/VMStyles";
import StorageFactory from "../../../Util/StorageFactory";
import { ACCESS_RESTRICTED_MESSAGE, SESSION_USER_ID_KEY } from "../../../Util/VMConstants";
import { checkIsPermissionExist } from "../../../Util/UtilHelper";
import SubscriptionDialog from "../../../Util/SubscriptionDialog";
import MUITypography from "../../../Components/MUITypography";
import { useSelector } from "react-redux";
import NoPermission from "../../../Components/NoPermission";

const ImageGalleryPanel = ({ enquiry_id, readOnly = false }) => {
  const [images, setImages] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
  const [deletingUrl, setDeletingUrl] = useState(null);

  const subscriptionStatus = useSelector(
    (state) => state.subscription.status
  );

    const {  permissions } = useSelector(
      (state) => state.subscription
    );
  

  const fileInputRef = useRef(null);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  

  /** 🔹 Fetch images */
  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_GET_EVENT_IMAGES}?user_id=${userID}&enquiry_id=${enquiry_id}`
      );
      if (res.data?.data?.image_urls) {
        setImages(res.data.data.image_urls);
      } else {
        setImages([]);
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [enquiry_id, userID]);

  useEffect(() => {
    if (!userID || !enquiry_id) return;
    fetchImages();
  }, [enquiry_id, fetchImages, userID]);

  /** 🔹 Handle file upload with compression */
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();

      for (let file of files) {
        // 🔹 compress each image before upload
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.3, // target ~300 KB
          maxWidthOrHeight: 1280, // resize to max 1280px
          useWebWorker: true,
        });
        formData.append("file", compressedFile);
      }

      formData.append("enquiry_id", enquiry_id);
      formData.append("user_id", userID);

      await axios.post(API_EVENT_PHOTO_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setProgress(
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            );
          }
        },
      });

      fetchImages(); // refresh after upload
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleUploadClick = () => {
              if (subscriptionStatus?.plan === 'premium' || subscriptionStatus?.plan === 'trial') {
                  fileInputRef.current.click();
                  setOpenSubscribeDialog(false);
                } else {
                  // open subscription dialog
                  setOpenSubscribeDialog(true);
                }
  }
  const handleImageClick = (img) => {
    setSelectedImage(img);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const handleDeletePhoto = async (imageUrl, e) => {
    e?.stopPropagation();
    if (!imageUrl || deletingUrl) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this photo?"
    );
    if (!confirmed) return;

    setDeletingUrl(imageUrl);
    try {
      const { data } = await axios.post(
        API_DELETE_EVENT_PHOTO,
        {
          user_id: userID,
          enquiry_id,
          image_url: imageUrl,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (data?.status_code === 200) {
        toast.success(data?.message || "Photo deleted successfully");
        if (selectedImage === imageUrl) {
          handleClose();
        }
        await fetchImages();
      } else {
        throw new Error(data?.message || "Failed to delete photo");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete photo";
      toast.error(message);
    } finally {
      setDeletingUrl(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "24px",
        width: "100%",
        maxWidth: "866px",
        height: "332px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <MUITypography variant="h4" gutterBottom   sx={{   fontWeight: 700, color: '#1E1E1E' ,flexGrow: 1}}>
          Event Photos
        </MUITypography>

        {
            !readOnly && checkIsPermissionExist(permissions, "VM038") &&
            <Button
              variant="contained"
              color="primary"
              disabled={uploading}
              sx={{
                minWidth: "140px",
                height: "40px",
                borderRadius: "6px",
                fontFamily: "Lato",
                fontSize: "15px",
                fontWeight: 500,
              }}
              onClick={handleUploadClick}
            >
              {uploading ? (
                <CircularProgress
                  size={20}
                  sx={{ color: "#fff" }}
                  value={progress}
                />
              ) : (
                "Upload"
              )}
            </Button>
        }


        {/* Hidden file input */}
        <input
          type="file"
          multiple
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>

      {/* Image Grid / No Records */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexWrap: "wrap",
          alignContent: "flex-start",
          gap: "16px",
          padding: "12px",
          backgroundColor: "#F9F9F9",
          borderRadius: "6px",
          overflowY: "auto",
          justifyContent: images.length === 0 ? "center" : "flex-start",
          alignItems: images.length === 0 ? "center" : "flex-start",
          position: "relative",
        }}
      >
        {loading ? (
          <CircularProgress size={28} />
        ) : images.length === 0 ? (
         <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              padding: "20px",
              color: "#888",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#F1F1F1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <ImageOutlinedIcon sx={{ fontSize: 28, color: "#B0B0B0" }} />
            </Box>

            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#444",
                fontFamily: "Lato",
              }}
            >
              No Images Uploaded
            </Typography>

            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "#888",
                mt: 0.5,
                fontFamily: "Lato",
              }}
            >
              Click "Upload" to add event photos.
            </Typography>
          </Box>
         
        ) : 
            checkIsPermissionExist(permissions, "VM034") ?
          images.map((img, idx) => (
            <Box
              key={`${img}-${idx}`}
              onClick={() => handleImageClick(img)}
              role="button"
              tabIndex={0}
              aria-label="View photo"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleImageClick(img);
                }
              }}
              sx={{
                position: "relative",
                width: "90px",
                height: "90px",
                borderRadius: "8px",
                backgroundColor: "#E0E0E0",
                backgroundImage: `url(${img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                overflow: "hidden",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                  "& .view-photo-overlay": {
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%)",
                  },
                  "& .delete-photo-btn": {
                    backgroundColor: "#D32F2F",
                    boxShadow: "0 2px 8px rgba(211,47,47,0.55)",
                    transform: "scale(1.08)",
                  },
                },
                "&:focus-visible": {
                  outline: "2px solid #1E3A8A",
                  outlineOffset: "2px",
                },
              }}
            >
              <Box
                className="view-photo-overlay"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)",
                  opacity: 1,
                  transition: "background 0.2s ease",
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    px: 1,
                    py: 0.5,
                    borderRadius: "4px",
                    backgroundColor: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  <VisibilityOutlinedIcon
                    sx={{ fontSize: 14, color: "#fff" }}
                  />
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 600,
                      fontFamily: "Lato",
                      letterSpacing: "0.4px",
                      lineHeight: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    View
                  </Typography>
                </Box>
              </Box>
              {!readOnly && checkIsPermissionExist(permissions, "VM038") && (
                <IconButton
                  className="delete-photo-btn"
                  size="small"
                  disabled={deletingUrl === img}
                  onClick={(e) => handleDeletePhoto(img, e)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    zIndex: 2,
                    opacity: 1,
                    backgroundColor: "rgba(211,47,47,0.92)",
                    color: "#fff",
                    width: 28,
                    height: 28,
                    border: "2px solid #fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                    transition: "background-color 0.2s, transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      backgroundColor: "#B71C1C",
                      boxShadow: "0 2px 10px rgba(183,28,28,0.6)",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(211,47,47,0.6)",
                      color: "#fff",
                    },
                  }}
                  aria-label="Delete photo"
                >
                  {deletingUrl === img ? (
                    <CircularProgress size={14} sx={{ color: "#fff" }} />
                  ) : (
                    <DeleteIcon sx={{ fontSize: 17 }} />
                  )}
                </IconButton>
              )}
            </Box>
          ))
          
        : <NoPermission message= {ACCESS_RESTRICTED_MESSAGE}/>
      
        }

        {/* 🔹 Overlay while uploading */}
        {uploading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
            }}
          >
            <CircularProgress size={36} />
            <Typography
              sx={{
                marginLeft: 2,
                fontSize: "14px",
                fontWeight: 500,
                color: "#555",
              }}
            >
              Uploading... {progress}%
            </Typography>
          </Box>
        )}
      </Box>




      <SubscriptionDialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        onSubscribe={() => {
          setOpenSubscribeDialog(false);
          window.location.href = '/subscription';
        }}
      />


      {/* Image Preview Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            maxWidth: "900px",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -44,
              left: 0,
              right: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 0.5,
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontSize: "15px",
                fontWeight: 600,
                fontFamily: "Lato",
              }}
            >
              Photo Preview
            </Typography>
            <IconButton
              onClick={handleClose}
              aria-label="Close preview"
              sx={{
                backgroundColor: "rgba(255,255,255,0.12)",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedImage && (
              <IconButton
                onClick={(e) => handleDeletePhoto(selectedImage, e)}
                disabled={deletingUrl === selectedImage}
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(211,47,47,0.9)",
                  color: "#fff",
                  zIndex: 2,
                  px: 2,
                  borderRadius: "6px",
                  gap: 1,
                  "&:hover": { backgroundColor: "rgba(183,28,28,0.95)" },
                }}
                aria-label="Delete photo"
              >
                {deletingUrl === selectedImage ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        fontFamily: "Lato",
                        textTransform: "none",
                      }}
                    >
                      Delete
                    </Typography>
                  </>
                )}
              </IconButton>
            )}

          {selectedImage && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: VM_Colors.white,
                borderRadius: "12px",
                p: 1,
                boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
              }}
            >
              <img
                src={selectedImage}
                alt="Event photo preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "75vh",
                  borderRadius: "8px",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ImageGalleryPanel;
